import {
  ProcessingItemState,
  useActionButtonContext,
} from '@aibees/elephant/context/action-button-context';
import { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people';
import { SaveRecordAction } from '@aibees/elephant/hooks';
import { useContactDetailsEnrichmentStatus } from '@aibees/elephant/hooks/people/use-contact-details-enrichment-status';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { SearchType } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';
import { useAuthContext } from '@aibees/shared/auth';
import { useNotify } from '@aibees/shared/hooks';
import { useWorkspaceUsers } from '@aibees/shared/workspace';
import { checkRevelationEmailState } from '../../people-contact-data/utils';
import { useIsPeopleUnlockButtonLoading } from '../../people-unlock/hooks/use-is-people-unlock-button-loading';
import { QUICK_INFO_BUTTONS } from '../components/quick-info-button';

export function useHandleContactDetailsEnrichment(
  data?: PeopleTableInfo,
  onSuccess?: () => void
) {
  const { user } = useAuthContext();
  const notify = useNotify();
  const users = useWorkspaceUsers(true);
  const { addProcessingItems } = useActionButtonContext();

  const api = useSaveRecord(SearchType.People);
  const { unlocked, failed, disabled } = useContactDetailsEnrichmentStatus(
    api,
    data
  );
  const { loadingContactDetails } = useIsPeopleUnlockButtonLoading(data);
  const { setProfileUpdateTriggered } = usePersonUpdatesStore();
  const isEmailUnlocked = checkRevelationEmailState(data);

  const handleEnrichContactDetails = ({
    updateMode,
    updateJobChangeMode,
  }: {
    updateMode?: boolean;
    updateJobChangeMode?: boolean;
  }): void => {
    if (loadingContactDetails) return;
    if (updateMode) {
      setProfileUpdateTriggered(true);
    }
    if (updateJobChangeMode && isEmailUnlocked) {
      addProcessingItems({
        id: data?.id ?? '',
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.CHECKING_FOR_UPDATE,
      });
    }

    const selectedUser = users.find((u) => u.id === user?.id);

    addProcessingItems({
      id: data?.id ?? '',
      type: QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
      state:
        updateMode || updateJobChangeMode
          ? ProcessingItemState.UPDATING
          : ProcessingItemState.UNLOCKING,
    });

    api
      .mutateAsync({
        kind: 'ADD',
        actions: [
          updateJobChangeMode
            ? SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE
            : updateMode
            ? SaveRecordAction.CONTACT_UPDATE_PROFILE
            : SaveRecordAction.CONTACT_REVEAL_PROFILE,
        ],
        ids: [data?.id || ''],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      })
      .then(() => {
        const key = Date.now().toString();
        notify.progress(
          'Your request is in progress',
          'Processing the profile insight. Please wait...',
          key,
          {
            icon: notificationIconMap.ProfileInsight,
          }
        );
        onSuccess?.();
      });
  };

  return {
    handleEnrichContactDetails,
    loading: loadingContactDetails,
    unlocked,
    failed,
    disabled,
  };
}
